import React, { useState } from "react";
import styled from "styled-components";
import { ButtonTertiaryCompact, UI_CAPTION_SEMIBOLD, colors2 } from "oolib";
import { FloppyDisk } from "@phosphor-icons/react";
import { Lightbox, LightboxTrigger, LightboxContent, useLightbox } from "@/components/UI/lightbox";
import { Button, Input, InputGroup } from "@/components/UI/CompactControls";
import TooltipV2 from "@/components/UI/Tooltip";
import { useCreateCohort, useGetCohorts } from "@/utils/react-query-hooks/cohorts";
import { findMatchingCohort } from "./findMatchingCohort";

const StyledPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.8rem;
  width: 24rem;
`;

const StyledRow = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
`;

const NameCohortPanel = ({ activeFilters }) => {
  const [name, setName] = useState("");
  const { setOpen } = useLightbox() || {};
  const { mutate: createCohort, isLoading } = useCreateCohort();

  const canSave = name.trim().length > 0 && !isLoading;

  const save = () => {
    if (!canSave) return;
    createCohort({ name: name.trim(), activeFilters }, { onSuccess: () => setOpen?.(false) });
  };

  return (
    <StyledPanel onClick={(e) => e.stopPropagation()}>
      <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey60 }}>
        Save current filters as a cohort
      </UI_CAPTION_SEMIBOLD>
      <StyledRow>
        <InputGroup style={{ flex: 1 }}>
          <Input
            autoFocus
            placeholder="Cohort name"
            value={name}
            style={{ textAlign: "left" }}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          />
        </InputGroup>
        <Button onClick={save} disabled={!canSave} style={{ opacity: canSave ? 1 : 0.5 }}>
          {isLoading ? "Saving…" : "Save"}
        </Button>
      </StyledRow>
    </StyledPanel>
  );
};

// FloppyDisk is not in oolib's icon map, so it's rendered as a child rather
// than via the `icon` prop; the style keeps the button square.
// A disabled native button swallows mouse events, so when disabled we also turn
// off its pointer events and let the surrounding tooltip wrapper take the hover.
const FloppyButton = ({ disabled }) => (
  <ButtonTertiaryCompact
    disabled={disabled}
    onClick={(e) => e.stopPropagation()}
    style={{ minWidth: 0, padding: "4px 6px", ...(disabled && { pointerEvents: "none" }) }}
  >
    <div style={{ display: "flex" }}>
      <FloppyDisk size={14} weight="bold" color={disabled ? colors2.grey40 : colors2.grey80} />
    </div>
  </ButtonTertiaryCompact>
);

/**
 * Icon-only "save as cohort" button. Sits left of the Clear button in the
 * filter headers; opens a small popover to name the cohort. Renders nothing
 * when there are no active filters to save, and is disabled when the current
 * selection is already saved as a cohort.
 */
export const SaveCohortButton = ({ activeFilters = [] }) => {
  const { data: cohorts } = useGetCohorts({ enabled: activeFilters.length > 0 });
  if (!activeFilters.length) return null;

  const matchingCohort = findMatchingCohort(cohorts, activeFilters);
  if (matchingCohort) {
    return (
      <TooltipV2 text={`Already saved as “${matchingCohort.name}”`} placement="bottom" wrapperDisplay="inline-flex">
        <FloppyButton disabled />
      </TooltipV2>
    );
  }

  return (
    <Lightbox placement="bottom-end">
      <LightboxTrigger>
        <FloppyButton />
      </LightboxTrigger>
      <LightboxContent style={{ zIndex: 99999 }}>
        <NameCohortPanel activeFilters={activeFilters} />
      </LightboxContent>
    </Lightbox>
  );
};
